import { Engine } from 'engine/Engine';
import { EngineContext } from 'engine/components/EngineProvider';
import { AudioManager } from 'engine/lib/AudioManager';
import { Message } from 'game/components/Message';
import { DialogMessage } from 'game/entities/MainWorld/dialog/DialogMessage';
import { DialogMessageUI } from 'game/entities/MainWorld/dialog/DialogMessageUI';
import { createStyles } from 'game/lib/createStyles';
import { inGameUIClasses } from 'game/styles/inGameUI';
import { colors, fonts } from 'game/styles/theme';
import { prefsStorage } from 'game/tutorial/prefs';
import { useContext, useEffect, useRef } from 'react';
import title from './dial-r-for-robot-apocalypse.jpg';

const wireIn1Src = require('game/sfx/wire-in-1.mp3');

// Safari needs the AudioContext to play as a *direct* result of a user interaction,
// otherwise it just puts it in a queue. When one *is* played as a direct result of a
// user interaction, Safari plays all that audio at once in an ear-shattering cacophony.
const wireIn1 = new AudioManager(wireIn1Src, { volume: 0.00001 });

const messages: DialogMessage[] = [
  {
    text: <Message text="Hey you," />,
    portrait: null,
  },
  {
    text: <Message text="It's safe to come out now." />,
    portrait: null,
  },
];

const classes = createStyles({
  container: {
    fontFamily: fonts.nunito,
    color: colors.white,
    fontSize: 24,
    textAlign: 'center',
    paddingTop: 30,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
  },
  main: {
    flexGrow: 1,
    maxHeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: '100%',
    height: '100%',
    background: `no-repeat center url(${title})`,
    backgroundSize: 'contain',
  },
  dialog: {
    position: 'relative',
    fontSize: 48,
    padding: 60,
  },
  instruction: {
    marginBottom: 50,
    '& > * + *': {
      marginTop: 10,
    },
  },
});

interface TitleScreenProps {
  onStart: (engine: Engine) => void,
}

const fadeDuration = 100;

export const TitleScreen: React.FC<TitleScreenProps> = ({ onStart }) => {
  const engine = useContext(EngineContext);
  const dialogStart = useRef(0);
  const messageIndex = useRef(0);

  useEffect(() => {
    const onClick = () => {
      if (prefsStorage.get().skipIntro) {
        onStart(engine);
        return;
      }
      if (!dialogStart.current) {
        wireIn1.play();
        dialogStart.current = engine.time.now;
      }
    };
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, [engine, onStart]);

  const deltaDialogStart = dialogStart.current ? engine.time.now - dialogStart.current : 0;

  return (
    <div className={classes.container}>
      <div className={classes.main}>
        {deltaDialogStart <= fadeDuration && (
          <div
            className={classes.logo}
            style={{
              opacity: dialogStart.current
                ? 1 - ((engine.time.now - dialogStart.current) / fadeDuration)
                : 1,
            }}
          >
            <h1 className={inGameUIClasses.srOnly}>
              Dial R for Robot Apocalypse
            </h1>
          </div>
        )}
        {deltaDialogStart >= 1000 && (
          <div className={classes.dialog}>
            <DialogMessageUI
              message={messages[messageIndex.current]}
              hasNext
              advance={() => {
                messageIndex.current++;
                if (messageIndex.current >= messages.length) {
                  onStart(engine);
                }
              }}
            />
          </div>
        )}
      </div>
      <div className={classes.instruction}>
        <div>
          <Message text="DEMO" />
        </div>
        <div>
          <Message text="Click anywhere to continue" />
        </div>
      </div>
    </div>
  );
};
